import React, { useRef } from 'react'
import { Animated, Pressable, StyleSheet, Text } from 'react-native'
import {
  CheckCircleOutlineIcon,
  CloseIcon,
  HighlightOffIcon,
  InfoIcon,
  ReportProblemIcon,
} from '@components/Icons'
import { colors } from '@lib/colors'
import { SnackbarMessage, SnackbarThemes } from '@context/SnackbarContext'

interface SnackbarProps {
  animationValue: any
  message: SnackbarMessage
  dismissSnackbar: () => void
}

export default function (props: SnackbarProps) {
  const { animationValue, message, dismissSnackbar } = props

  /**
   * The following three constants constitute a hackish way to persist
   * shadow positioning, since the offsets are reset for unknown reasons.
   */
  const shadowOffsetHack = useRef(new Animated.Value(1))
  const shadowOffset = shadowOffsetHack.current.interpolate({
    inputRange: [0, 1],
    outputRange: [0, 5],
  })
  const shadow = {
    shadowColor: colors.black,
    shadowOffset: {
      width: 0,
      height: shadowOffset,
    },
    shadowOpacity: 0.1,
    shadowRadius: 5,
  }
  // The funky stuff ends here. The rest is fairly normal

  const opacity = animationValue
  const translationY = animationValue.interpolate({
    inputRange: [0, 1],
    outputRange: [5, 0],
  })

  const animatedStyles = {
    opacity: opacity,
    transform: [{
      translateY: translationY,
    }],
    ...shadow,
  }

  const colorStyles = () => {
    switch (message.theme) {
      case SnackbarThemes.error:
        return { backgroundColor: colors.red[200] }
      case SnackbarThemes.warning:
        return { backgroundColor: colors.yellow[200] }
      case SnackbarThemes.success:
        return { backgroundColor: colors.green[200] }
      default:
        return { backgroundColor: colors.blue[200] }
    }
  }

  const iconComponent = () => {
    const iconProps = { size: 24, color: colors.white }

    switch (message.theme) {
      case SnackbarThemes.error:
        return <HighlightOffIcon {...iconProps} />
      case SnackbarThemes.warning:
        return <ReportProblemIcon {...iconProps} />
      case SnackbarThemes.success:
        return <CheckCircleOutlineIcon {...iconProps} />
      default:
        return <InfoIcon {...iconProps} />
    }
  }

  return (
    <Animated.View style={[styles.wrapper, animatedStyles, colorStyles()]}>
      { iconComponent() }
      <Text style={ styles.text }>{ message.text }</Text>
      <Pressable onPress={ dismissSnackbar }>
        <CloseIcon size={20} color={ colors.white }/>
      </Pressable>
    </Animated.View>
  )
}

const styles = StyleSheet.create({
  wrapper: {
    position: 'absolute',
    bottom: 60,
    left: 10,
    right: 10,
    padding: 20,
    borderRadius: 3,
    flexDirection: 'row',
    alignItems: 'center',
    zIndex: 10,
  },
  text: {
    color: colors.white,
    fontSize: 16,
    fontWeight: '500',
    textAlign: 'left',
    flex: 1,
    marginLeft: 10,
    marginRight: 10,
  },
})
